import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Paperclip, X, Upload } from "lucide-react";

const ACCEPT = "application/pdf,image/*,audio/*,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt";

const detectKind = (name = "", type = "") => {
  const n = name.toLowerCase(); const t = (type || "").toLowerCase();
  if (t.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(n)) return "image";
  if (t.startsWith("audio/") || /\.(mp3|m4a|wav|ogg)$/i.test(n)) return "audio";
  if (t === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  return "other";
};

// uploadPath: "/coach/upload" | "/learner/upload"
export default function MessageComposer({ uploadPath, onSend, placeholder = "Écrivez votre message…", submitLabel = "Envoyer" }) {
  const [text, setText] = useState("");
  const [att, setAtt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtt({ attachment_path: data.path, attachment_name: f.name, attachment_kind: detectKind(f.name, f.type) });
      toast.success("Fichier joint");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const send = async () => {
    if (!text.trim() && !att) return toast.error("Message vide");
    setSending(true);
    try {
      await onSend({ text: text.trim(), ...(att || {}) });
      setText(""); setAtt(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="border-t border-[#E6E1DA] bg-white p-3">
      {att && (
        <div className="mb-2 inline-flex items-center gap-2 text-xs text-[#2A5B52] bg-[#F1F6F2] rounded-lg px-2 py-1" data-testid="composer-attachment">
          <Paperclip className="w-3 h-3" /> {att.attachment_name}
          <button onClick={() => setAtt(null)} data-testid="composer-remove-attachment"><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" onChange={upload} className="hidden" accept={ACCEPT} data-testid="composer-file-input" />
        <Button type="button" variant="outline" size="icon" className="bg-white shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading} title="Joindre un fichier" data-testid="composer-attach-button">
          {uploading ? <Upload className="w-4 h-4 animate-pulse" /> : <Paperclip className="w-4 h-4" />}
        </Button>
        <Textarea
          data-testid="composer-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={placeholder}
          rows={1}
          className="min-h-[44px] max-h-32 resize-none bg-[#FAF8F5]"
        />
        <Button type="button" onClick={send} disabled={sending} className="shrink-0 bg-[#D95D39] hover:bg-[#C04E2D] text-white" data-testid="composer-send-button">
          <Send className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{submitLabel}</span>
        </Button>
      </div>
    </div>
  );
}
